import { Observable } from 'rxjs/internal/Observable';
import { Injectable, Optional, Inject, InjectionToken, OnDestroy } from '@angular/core';
import { Subject, Subscription, pipe } from 'rxjs';
import { EventManager } from '@angular/platform-browser';
import { filter, shareReplay } from 'rxjs/operators';

export interface IMessageData {
  type: string;
  data?: any;
  id?: number;
}
export interface IWindowMessage extends IMessageData {
  id: number;
}
export interface IMessageConverter {
  execute: (e: MessageEvent) => IMessageData;
}

export const WINDOW_MESSAGE_CONVERTERS = new InjectionToken<IMessageConverter>('direct.window.message.converters');

/**
 * Provides the stream of window messages
 * that have been converted to general interface
 * Converters should be injected by modules, that integrate with the message source
 */
@Injectable({ providedIn: 'root' })
export class WindowCommunicatorService implements OnDestroy {
  private subscriptions: Subscription;
  private stream: Subject<IWindowMessage>;
  private streamBuffer: Observable<IWindowMessage>;
  private requestId = 0;
  private isInitialized = false;

  constructor(
    private eventManager: EventManager,
    @Optional() @Inject(WINDOW_MESSAGE_CONVERTERS) private messageConverters: IMessageConverter[] = []
  ) {
    this.subscriptions = new Subscription();
    this.stream = new Subject();
    this.streamBuffer = this.stream.pipe(shareReplay(1));
  }

  private onMessage(e: MessageEvent) {
    for (const converter of this.messageConverters) {
      const message = converter.execute(e);
      if (message) {
        const id = message.id === undefined ? ++this.requestId : message.id;
        this.stream.next({ ...message, id });
        return;
      }
    }
  }

  private messageFilter(messageTypes: string[]) {
    return messageTypes.length
      ? pipe(filter((message: IWindowMessage) => messageTypes.indexOf(message.type) > -1))
      : null;
  }

  init() {
    if (!this.isInitialized) {
      this.subscriptions.add(window.addEventListener('message', this.onMessage.bind(this)));
    }
    this.isInitialized = true;
  }

  observe(messageTypes: string[] = []): Observable<IWindowMessage> {
    return this.stream.pipe(this.messageFilter(messageTypes));
  }

  observeWithLast(messageTypes: string[] = []) {
    return this.streamBuffer.pipe(this.messageFilter(messageTypes));
  }

  postMessage(message: IWindowMessage) {
    window.postMessage(message, '*');
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
  }
}
